import React, { useState, useEffect } from 'react';
import { healthAPI, customersAPI, foodAPI, groupsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { 
  Users, Utensils, FolderTree, MessageSquare, 
  TrendingUp, Activity, ArrowUpRight
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const StatCard = ({ icon: Icon, label, value, trend, color }) => (
  <div className="panel border border-border hover:shadow-md transition-all">
    <div className="flex items-start justify-between">
      <div className={`w-12 h-12 rounded-md ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
      </div>
      {trend && (
        <div className="flex items-center gap-1 text-primary text-sm font-medium">
          <ArrowUpRight className="w-4 h-4" />
          <span>{trend}</span>
        </div>
      )}
    </div>
    <div className="mt-4">
      <p className="text-3xl font-bold text-foreground">{value}</p>
      <p className="text-muted-foreground text-sm mt-1">{label}</p>
    </div>
  </div>
);

const DashboardPage = () => {
  const [stats, setStats] = useState({
    customers: 0,
    foods: 0,
    categories: 0,
    groups: 0
  });
  const [loading, setLoading] = useState(true);
  const [apiStatus, setApiStatus] = useState('checking');

  // Sample chart data
  const chartData = [
    { name: 'Mon', users: 40, foods: 24 },
    { name: 'Tue', users: 30, foods: 13 },
    { name: 'Wed', users: 20, foods: 38 },
    { name: 'Thu', users: 27, foods: 39 },
    { name: 'Fri', users: 18, foods: 48 },
    { name: 'Sat', users: 23, foods: 38 },
    { name: 'Sun', users: 34, foods: 43 },
  ];

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Check API health
        const healthResponse = await healthAPI.check();
        setApiStatus(healthResponse.data.status);

        // Fetch stats
        const [customersRes, foodsRes, categoriesRes, groupsRes] = await Promise.all([
          customersAPI.getAll({ limit: 1 }).catch(() => ({ data: { total: 0 } })),
          foodAPI.getItems({ limit: 1 }).catch(() => ({ data: { total: 0 } })),
          foodAPI.getCategories().catch(() => ({ data: { categories: [] } })),
          groupsAPI.getAll().catch(() => ({ data: { groups: [] } }))
        ]);

        setStats({
          customers: customersRes.data?.total || 0,
          foods: foodsRes.data?.total || 0,
          categories: categoriesRes.data?.categories?.length || 0,
          groups: groupsRes.data?.groups?.length || 0
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
        setApiStatus('error');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Welcome to WingsDiet Admin</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${apiStatus === 'healthy' ? 'bg-emerald-500' : apiStatus === 'checking' ? 'bg-amber-500' : 'bg-red-500'}`} />
            <span className="text-sm text-muted-foreground">
              API: {apiStatus === 'healthy' ? 'Connected' : apiStatus === 'checking' ? 'Checking...' : 'Disconnected'}
            </span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            icon={Users}
            label="Total Customers"
            value={loading ? '...' : stats.customers}
            trend="+12%"
            color="bg-primary"
          />
          <StatCard
            icon={Utensils}
            label="Food Items"
            value={loading ? '...' : stats.foods}
            trend="+8%"
            color="bg-secondary"
          />
          <StatCard
            icon={FolderTree}
            label="Categories"
            value={loading ? '...' : stats.categories}
            color="bg-secondary"
          />
          <StatCard
            icon={MessageSquare}
            label="Chat Groups"
            value={loading ? '...' : stats.groups}
            color="bg-primary"
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Activity Chart */}
          <div className="panel border border-border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground">User Activity</h3>
                <p className="text-muted-foreground text-sm">Weekly overview</p>
              </div>
              <Activity className="w-5 h-5 text-primary" />
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="name" stroke="#A8A29E" fontSize={12} />
                  <YAxis stroke="#A8A29E" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #E7E5E4', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="users"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#colorUsers)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Food Logs Chart */}
          <div className="panel border border-border">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Food Logs</h3>
                <p className="text-muted-foreground text-sm">Daily food entries</p>
              </div>
              <TrendingUp className="w-5 h-5 text-secondary" />
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorFoods" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="name" stroke="#A8A29E" fontSize={12} />
                  <YAxis stroke="#A8A29E" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'white', 
                      border: '1px solid #E7E5E4', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="foods"
                    stroke="#F59E0B"
                    strokeWidth={2}
                    fill="url(#colorFoods)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Quick Info */}
        <div className="panel border border-border bg-card">
          <h3 className="text-lg font-semibold text-foreground mb-3">Platform Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Backend:</span>
              <span className="ml-2 text-foreground font-medium">FastAPI (Python)</span>
            </div>
            <div>
              <span className="text-muted-foreground">Frontend:</span>
              <span className="ml-2 text-foreground font-medium">React.js</span>
            </div>
            <div>
              <span className="text-muted-foreground">Database:</span>
              <span className="ml-2 text-foreground font-medium">MongoDB</span>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
